import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import he from 'he';
import { decode } from 'html-entities';

import { setEndOfContenteditable } from 'utils/helpers';
import { useStateBasedOnProps } from 'utils/hooks';

import './MultiLineInput.styles.css';

const encodeValue = value => {
    return he.encode(value, {
        useNamedReferences: true
    });
}

interface MultiLineInputProps {
    className?: string,
    value?: string,
    placeholder?: string,
    canEdit?: boolean,
    autoFocus?: boolean,
    autoFocusDelay?: number,
    blurOnEnter?: boolean,
    withEncode?: boolean,
    spellCheck?: boolean,
    onChange?: (value: string, event: React.ChangeEvent) => void,
    onBlur?: (value: string, event: React.FocusEvent) => void,
    onFocus?: (event: React.FocusEvent) => void,
    onKeyDown?: (event: React.KeyboardEvent) => void,
    onPaste?: (event: ClipboardEvent, value: string) => void,
    onCopy?: (event: Event) => void,
    onCut?: (event: Event) => void,
    onSelectionChange?: (event: Event) => void,
    provideController?: any
}

const MultiLineInput = forwardRef(({ className, value, placeholder, canEdit, autoFocus, autoFocusDelay = 0, blurOnEnter = true, withEncode = true, spellCheck, onChange, onBlur, onFocus, onKeyDown, onPaste, onCopy, onCut, onSelectionChange, provideController }: MultiLineInputProps, ref) => {
    const [currentValue, setCurrentValue] = useStateBasedOnProps(value);
    const elementRef = useRef(null);

    const focus = useCallback(atEnd => {
        if (elementRef.current) {
            elementRef.current.focus();
            if (atEnd && elementRef.current.textContent) {
                const target = elementRef.current.childNodes[elementRef.current.childNodes.length - 1] || elementRef.current;
                setEndOfContenteditable(target);
            }
        }

    }, [elementRef]);

    const blur = useCallback(() => {
        elementRef.current && elementRef.current.blur();
    }, [elementRef]);

    const setValue = useCallback(nextValue => {
        if (elementRef.current) {
            elementRef.current.innerHTML = nextValue;
        }

        setCurrentValue(nextValue);
    }, [elementRef]);

    const reset = useCallback(setOriginalValue => {
        setValue(setOriginalValue ? value : '');
    }, [setValue, value]);

    const getValue = useCallback(() => {
        return decode(elementRef.current?.innerHTML).replace(/\u200B/g, '');
    }, [elementRef]);

    const handleChange = useCallback(event => {
        const brs = elementRef.current?.getElementsByTagName('br');
        const divs = elementRef.current?.getElementsByTagName('div');
        const fonts = elementRef.current?.getElementsByTagName('font');

        if (brs.length) {
            Array.from(brs).forEach(br => br.parentNode.removeChild(br));
        }

        if (divs.length) {
            Array.from(divs).forEach(div => div.textContent === '' && div.className === '' && div.parentNode.removeChild(div));
        }

        if (fonts.length) {
            Array.from(fonts).forEach(el => el.parentNode.replaceChild(document.createTextNode(el.textContent), el));
        }


        const nextValue = getValue();

        if (nextValue === '\n') {
            setValue('');
            return;
        }

        setCurrentValue(nextValue);
        onChange && onChange(nextValue, event);
    }, [getValue, setCurrentValue, onChange]);

    const handleSelectionChange = useCallback(event => {
        const nextValue = getValue();

        setCurrentValue(nextValue);
        onSelectionChange && onSelectionChange(event);
    }, [getValue, setCurrentValue, onSelectionChange]);

    const handleBlur = useCallback(event => {
        onBlur && onBlur(currentValue, event);
    }, [onBlur, currentValue]);

    const handleKeyDown = useCallback(event => {
        if (event.which === 13 && !event.shiftKey && blurOnEnter) {
            blur();
        }

        onKeyDown && onKeyDown(event);

        // setCurrentValue(elementRef.current.innerHTML);
    }, [blur, onKeyDown]);

    const handlePaste = useCallback(async event => {
        event.preventDefault();

        const nextValue = (event.originalEvent || event).clipboardData.getData('text/plain');

        if (onPaste) {
            const handled = await onPaste(event, nextValue);

            if (handled) {
                setCurrentValue(getValue());
                return;
            }
        }

        window.document.execCommand('insertText', false, nextValue);
    }, [onPaste]);

    const handleDocSelectionChange = () => {
        const selection = window.getSelection();

        if (elementRef.current?.contains(selection.focusNode)) {
            handleSelectionChange();
        }
    }

    const empty = useMemo(() => currentValue === '' || currentValue === '\n', [currentValue]);
    const isEmpty = useCallback(() => {
        const val = getValue();
        return val === '' || val === '\n';
    }, [getValue]);

    const ctrl = useMemo(() => {
        return {
            focus: atEnd => {
                focus(atEnd);
            },
            blur: () => {
                blur();
            },
            reset: setOriginalValue => {
                reset(setOriginalValue);
            },
            setValue: nextValue => {
                setValue(nextValue);
            },
            getElement: () => elementRef.current,
            isEmpty,
            getValue
        };
    }, [focus, blur, reset, setValue, isEmpty, getValue]);

    useEffect(() => {

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, []);

    useEffect(() => {
        if (autoFocus) {
            setTimeout(() => {
                focus(true);
            }, autoFocusDelay)
        }

        document.addEventListener('selectionchange', handleDocSelectionChange);

        return () => {
            document.removeEventListener('selectionchange', handleDocSelectionChange);
        }
    }, []);

    useImperativeHandle(ref, () => ctrl, [ctrl]);

    return (
        <div
            className={'ui-field-input multi-line-input ' + className + (empty ? ' empty' : '')}
            contentEditable={canEdit}
            placeholder={placeholder}
            spellCheck={spellCheck}
            ref={elementRef}
            dangerouslySetInnerHTML={{ __html: withEncode ? encodeValue(value) : value }}
            onInput={handleChange}
            onFocus={onFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onCopy={onCopy}
            onCut={onCut}
        />
    );
});

MultiLineInput.defaultProps = {
    className: '',
    value: ''
}

export default MultiLineInput;
